'use client';

import {useEffect,useState} from 'react';
import {createPortal} from 'react-dom';
import {usePathname} from 'next/navigation';
import {LayoutGrid,MessageCircle,Image,Video,PenLine,Code2,Search,BriefcaseBusiness,AudioLines,Palette,MessagesSquare,Sparkles,Inbox,Bot,Tags,Gift,Scale,Braces,Percent,Calculator} from 'lucide-react';
import SiteFooter from './SiteFooter';
import Engagement from './Engagement';
import ShareButtons from './ShareButtons';
import type {SiteSettings,Placement} from '@/lib/site';
import type {Database} from '@/lib/types';

type Lang='zh'|'en'|'ja'|'es';
const names={zh:['工具分类','全部工具','AI 对话助手','AI 图像工具','AI 视频工具','AI 写作文案','AI 编程工具','AI 搜索引擎','AI 办公效率','AI 音频音乐','AI 设计工具','AI 智能体','AI 开放收录','AI 提示词','AI 技能库'],en:['Tool Categories','All Tools','AI Assistants','AI Image Tools','AI Video Tools','AI Copywriting','AI Coding Tools','AI Search Engines','AI Productivity','AI Audio & Music','AI Design Tools','AI Agents','AI Open Submissions','AI Prompts','AI Skills'],ja:['ツール分類','すべてのツール','AIアシスタント','AI画像ツール','AI動画ツール','AIコピーライティング','AIコーディング','AI検索エンジン','AI業務効率化','AI音声・音楽','AIデザイン','AIエージェント','AI 一般投稿','AIプロンプト','AIスキル'],es:['Categorías','Todas las herramientas','Asistentes IA','Imagen IA','Vídeo IA','Copywriting IA','Programación IA','Buscadores IA','Productividad IA','Audio y música IA','Diseño IA','Agentes IA','Envío Abierto de IA','Prompts de IA','Habilidades de IA']} as const;
const defs=[['chat',MessageCircle],['image',Image],['video',Video],['writing',PenLine],['code',Code2],['search',Search],['office',BriefcaseBusiness],['audio',AudioLines],['design',Palette],['agents',Bot],['submit',Inbox]] as const;
const pricingGuide={zh:['AI工具收费指南','AI工具价格大全','免费AI工具大全','AI工具价格对比','AI API价格大全','AI工具优惠信息','AI工具成本分析'],en:['AI Tool Pricing Guide','AI Tool Pricing','Free AI Tools','AI Tool Price Comparison','AI API Pricing','AI Tool Deals','AI Tool Cost Analysis'],ja:['AIツール料金ガイド','AIツール料金一覧','無料AIツール','AIツール料金比較','AI API料金','AIツール割引情報','AIツールコスト分析'],es:['Guía de precios de IA','Precios de herramientas IA','Herramientas IA gratuitas','Comparación de precios','Precios de API de IA','Ofertas de herramientas IA','Análisis de costes']} as const;
const pricingSubs=[['pricing',Tags],['pricing/free',Gift],['pricing/compare',Scale],['pricing/api',Braces],['pricing/deals',Percent],['pricing/cost',Calculator]] as const;

function Ad({item,className}:{item?:Placement;className:string}){
  if(!item?.image)return null;
  return <a className={className} href={item.link||'#'}><img src={item.image} alt={item.title} width="1200" height="200" loading="lazy" decoding="async"/></a>;
}

function CategoryPanel({lang,left,data,mobile=false,path}:{lang:Lang;left?:Placement;data:Database;mobile?:boolean;path:string}){
  const prefix=`/${lang}`;
  const isPricing=path.startsWith('/pricing');
  const published=data.tools.filter(x=>x.status==='published');
  const count=(category:string)=>published.filter(x=>x.category===category).length;
  return <aside id={mobile?'categories-mobile':undefined} className={`global-aside categories-panel ${mobile?'global-inline':'global-fixed'}`}>
    <Ad item={left} className="sidebar-ad"/>
    {isPricing
      ? <><b className="side-title">{pricingGuide[lang][0]}</b>{pricingSubs.map(([slug,Icon],i)=><a href={`${prefix}/${slug}`} key={slug}><Icon/>{pricingGuide[lang][i+1]}</a>)}</>
      : <><a href={`${prefix}/category/all`}><LayoutGrid/>{names[lang][1]}<small>{published.length}</small></a>{defs.map(([id,Icon],i)=><a href={`${prefix}/category/${id}`} key={id}><Icon/><span>{names[lang][i+2]}</span><small>{count(id)}</small></a>)}<a href="https://chatgptskill.cn/skills" target="_blank" rel="noreferrer"><Sparkles/>{names[lang][14]} <small>↗</small></a><a href="https://chatgptskill.cn/prompts" target="_blank" rel="noreferrer"><MessagesSquare/>{names[lang][13]} <small>↗</small></a></>}
  </aside>;
}

export default function GlobalChrome(){
  const p=usePathname(),path=p.replace(/^\/(zh|en|ja|es)(?=\/|$)/,'')||'/',pathLang=p.split('/')[1] as Lang;
  const [lang,setLang]=useState<Lang>('en'),[site,setSite]=useState<SiteSettings>({banners:[],ads:[]}),[data,setData]=useState<Database>({categories:[],tools:[]});
  const [articleHost,setArticleHost]=useState<HTMLElement|null>(null),[homeSideHost,setHomeSideHost]=useState<HTMLElement|null>(null),[bannerHost,setBannerHost]=useState<HTMLElement|null>(null),[mobileSidebarHost,setMobileSidebarHost]=useState<HTMLElement|null>(null),[isMobile,setIsMobile]=useState(false);

  useEffect(()=>{
    setLang(names[pathLang]?pathLang:'en');
    fetch('/api/settings').then(r=>r.json()).then(setSite).catch(()=>{});
    fetch('/api/tools?summary=1').then(r=>r.json()).then(setData).catch(()=>{});
    const media=matchMedia('(max-width:800px)');
    const syncMobile=()=>setIsMobile(media.matches);
    syncMobile();
    media.addEventListener('change',syncMobile);

    let mobileHost:HTMLElement|null=null;
    if(path!=='/'&&!path.startsWith('/lidalu1987@')&&!path.startsWith('/contributor')&&!path.startsWith('/practice')&&!path.startsWith('/rankings')){
      const main=document.querySelector('main');
      if(main){
        mobileHost=document.createElement('div');
        mobileHost.className='mobile-sidebar-host';
        main.prepend(mobileHost);
        setMobileSidebarHost(mobileHost);
      }
    }

    const timer=setTimeout(()=>{
      const article=document.querySelector('article.prose');
      if(article?.parentNode){
        document.querySelector('.prose-ad-host')?.remove();
        const h=document.createElement('div');h.className='prose-ad-host';article.parentNode.insertBefore(h,article);setArticleHost(h);
      }
      const side=document.querySelector('.directory #categories');
      if(side){const h=document.createElement('div');h.className='home-side-ad-host';side.prepend(h);setHomeSideHost(h)}
      const banners=document.querySelector('.home-banners');
      if(banners){const h=document.createElement('div');h.className='banner-two-host';banners.append(h);setBannerHost(h)}
    },0);

    return()=>{
      clearTimeout(timer);
      media.removeEventListener('change',syncMobile);
      document.querySelector('.prose-ad-host')?.remove();
      document.querySelector('.home-side-ad-host')?.remove();
      document.querySelector('.banner-two-host')?.remove();
      mobileHost?.remove();
      setMobileSidebarHost(null);
    };
  },[p,path,pathLang]);

  if(path.startsWith('/lidalu1987@')||path.startsWith('/contributor'))return null;
  const m=path.match(/^\/(tools|news|knowledge|scenarios)\/([^/]+)$/),middle=site.ads.find(x=>x.id==='detail-middle'),bottom=site.ads.find(x=>x.id==='detail-bottom'),left=site.ads.find(x=>x.id==='sidebar-left'),banner2=site.banners.find(x=>x.id==='home-2');
  return <>
    {path!=='/'&&!path.startsWith('/practice')&&!path.startsWith('/rankings')&&!isMobile&&<CategoryPanel lang={lang} left={left} data={data} path={path}/>}
    {path!=='/'&&!path.startsWith('/practice')&&!path.startsWith('/rankings')&&isMobile&&mobileSidebarHost&&createPortal(<CategoryPanel lang={lang} left={left} data={data} mobile path={path}/>,mobileSidebarHost)}
    {articleHost&&createPortal(<Ad item={middle} className="prose-ad"/>,articleHost)}
    {homeSideHost&&createPortal(<Ad item={left} className="sidebar-ad"/>,homeSideHost)}
    {bannerHost&&banner2&&createPortal(<Ad item={banner2} className="home-banner-extra"/>,bannerHost)}
    {m&&<div className="global-share"><ShareButtons lang={lang}/></div>}
    {m&&bottom&&<div className="detail-bottom-ad"><Ad item={bottom} className="prose-ad"/></div>}
    {m&&<div className="global-engage"><Engagement targetId={m[2]} targetType={m[1]==='tools'?'tool':'article'}/></div>}
    <SiteFooter/>
  </>;
}
